
import { memo, useCallback, useState } from 'react';
import { Play, Workflow as WorkflowIcon, Settings, Bot, Wrench, Brain, AlertTriangle, CheckCircle2, XCircle, Clock, Layers, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { WorkflowCard } from '../WorkflowCard';
import { StateBadges } from '../StateBadges';
import { BreakpointMarker } from '../BreakpointMarker';
import { useNodeSettings } from '../NodeSettingsContext';
import { type StateNodeData, type CapabilityType, type ParallelBranchInfo } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';
import type { AggregatedCheckpointData } from '@/types/workflow-replay';
import { capabilityColors, defaultTaskColors } from '@/lib/capability-colors';
import { buildWorkflowEntityOpenRequest } from '../utils/qualify-workflow-resource';

interface TaskNodeProps {
    id: string;
    data: StateNodeData;
    selected?: boolean;
    /** Callback when user clicks to navigate into a sub-workflow */
    onNavigateToSubWorkflow?: (workflowKey: string) => void;
}

// Resource type detection and display configuration
type ResourceDisplayType = CapabilityType | 'task';

/**
 * Determine the resource display type for a Task node.
 * 
 * Uses the explicit resourceType from node data (set during ASL conversion via registry lookup).
 * Falls back to generic 'task' if type is not set.
 * 
 * NOTE: Does NOT infer type from resource string - type comes from registry lookup during conversion.
 */
function getResourceType(resourceType?: CapabilityType): ResourceDisplayType {
    // Use explicit resourceType from node data (set during ASL conversion)
    if (resourceType) return resourceType;
    
    // Unknown type - display as generic task
    return 'task';
}

function stripToolMethodSuffix(resourceRef: string): string {
    const methodSep = resourceRef.indexOf('::');
    return methodSep >= 0 ? resourceRef.slice(0, methodSep) : resourceRef;
}

function parseCapabilityDisplay(resourceType: ResourceDisplayType, resourceRef: string): { capabilityName: string; appKey?: string } {
    if (resourceType !== 'agent' && resourceType !== 'tool') {
        return { capabilityName: resourceRef };
    }

    const normalizedRef = resourceType === 'tool' ? stripToolMethodSuffix(resourceRef) : resourceRef;
    if (!normalizedRef.includes('/')) {
        return { capabilityName: normalizedRef };
    }

    const [appKey, capabilityName] = normalizedRef.split('/', 2);
    return {
        capabilityName: capabilityName || normalizedRef,
        appKey: appKey || undefined,
    };
}

const resourceConfig: Record<ResourceDisplayType, { 
    icon: React.ReactNode; 
    bgColor: string; 
    textColor: string;
    label: string;
    accentColor: string;
    handleColor: string;
}> = {
    agent: { 
        icon: <Bot size={20} />, 
        bgColor: capabilityColors.agent.bgLight, 
        textColor: capabilityColors.agent.text,
        label: 'Agent',
        accentColor: capabilityColors.agent.border,
        handleColor: `${capabilityColors.agent.handleBg} ${capabilityColors.agent.handleBorder}`,
    },
    tool: { 
        icon: <Wrench size={20} />, 
        bgColor: capabilityColors.tool.bgLight, 
        textColor: capabilityColors.tool.text,
        label: 'Tool',
        accentColor: capabilityColors.tool.border,
        handleColor: `${capabilityColors.tool.handleBg} ${capabilityColors.tool.handleBorder}`,
    },
    model: { 
        icon: <Brain size={20} />, 
        bgColor: capabilityColors.model.bgLight, 
        textColor: capabilityColors.model.text,
        label: 'Model',
        accentColor: capabilityColors.model.border,
        handleColor: `${capabilityColors.model.handleBg} ${capabilityColors.model.handleBorder}`,
    },
    workflow: { 
        icon: <WorkflowIcon size={20} />, 
        bgColor: capabilityColors.workflow.bgLight, 
        textColor: capabilityColors.workflow.text,
        label: 'Workflow',
        accentColor: capabilityColors.workflow.border,
        handleColor: `${capabilityColors.workflow.handleBg} ${capabilityColors.workflow.handleBorder}`,
    },
    task: { 
        icon: <Play size={20} />, 
        bgColor: defaultTaskColors.bgLight, 
        textColor: defaultTaskColors.text,
        label: 'Task',
        accentColor: defaultTaskColors.border,
        handleColor: `${defaultTaskColors.handleBg} ${defaultTaskColors.handleBorder}`,
    },
};

/**
 * Task Node - Represents an ASL Task state.
 * 
 * Displays:
 * - State name + Type pill
 * - Resource/ARN or sub-workflow reference
 * - Feature badges: Retry, Catch, Timeout, Heartbeat, data transforms
 * - Settings button and sub-workflow navigation
 * - Different icons for: Agent, Tool, Model, Workflow, generic Task
 */
export const TaskNode = memo(function TaskNode({ id, data, selected }: TaskNodeProps) {
    const { openSettings } = useNodeSettings();
    const [isExpanded, setIsExpanded] = useState(false);
    const { 
        label, 
        state, 
        isStart, 
        isEnd, 
        resourceType: explicitResourceType, 
        resourceAppKey,
        resourceRegistryMatch,
        resourceError, 
        layoutDirection,
        isDebugMode,
        isCurrentNode,
        hasBreakpoint,
        onToggleBreakpoint,
        // Replay mode props
        isReplayMode,
        replayCheckpoint,
        replayStatus,
        replayAggregated,
        isMapIterationNode,
        parentMapNodeId,
        parentParallelInfo,
        onReplayNodeClick,
        // Sub-workflow replay props
        childRunId,
        hasChildRunForReplay,
        // Tiled window callback
        onOpenTiledWindow,
        // Dynamic routing props
        isDynamicRoute,
        isDynamicNextConfigured,
        onDynamicHandleClick,
    } = data as StateNodeData & {
        replayAggregated?: AggregatedCheckpointData | null;
        isMapIterationNode?: boolean;
        parentMapNodeId?: string;
        parentParallelInfo?: ParallelBranchInfo;
    };

    // Toggle expansion state
    const handleToggleExpand = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded((prev) => !prev);
    }, []);

    // Get resource type from node data (set during ASL conversion via registry lookup)
    const resourceType = getResourceType(explicitResourceType);
    
    // Check if this task references a sub-workflow
    const isSubWorkflow = resourceType === 'workflow';

    // Extract clean resource name for badge display (remove prefix)
    // Handle both static Resource and dynamic Resource.$ (JSONPath reference)
    const staticResource = state?.Resource?.replace(/^(agent|tool|model|workflow):/, '') || null;
    const dynamicResource = state?.['Resource.$'] as string | undefined;
    const resourceName = staticResource || dynamicResource || null;
    const parsedCapability = resourceName ? parseCapabilityDisplay(resourceType, resourceName) : null;
    const capabilityName = parsedCapability?.capabilityName ?? resourceName;
    const capabilityAppKey = parsedCapability?.appKey ?? resourceAppKey;

    // Handle click to open tiled window for drill-down navigation
    // In replay mode with a child run, passes the replayRunId for sub-workflow replay
    const handleOpenInTiledWindow = useCallback(() => {
        if (!onOpenTiledWindow || !state?.Resource) return;

        const request = buildWorkflowEntityOpenRequest(
            state.Resource,
            resourceType,
            {
                appKey: capabilityAppKey ?? resourceAppKey,
                resourceAppKey,
                entityName: label,
            },
        );
        if (!request) return;

        onOpenTiledWindow({
            ...request,
            // Pass replay run ID for sub-workflow navigation in replay mode
            replayRunId: isReplayMode && isSubWorkflow && childRunId ? childRunId : undefined,
        });
    }, [
        onOpenTiledWindow,
        state?.Resource,
        resourceType,
        label,
        capabilityAppKey,
        resourceAppKey,
        isReplayMode,
        isSubWorkflow,
        childRunId,
    ]);

    // Handle card click - opens replay inspector in replay mode, or tiled window for drill-down in normal mode
    const handleCardClick = useCallback(() => {
        // In replay mode, always show the node inspector (use the link button to navigate to child workflows)
        if (isReplayMode && onReplayNodeClick) {
            onReplayNodeClick(id, parentMapNodeId, parentParallelInfo);
            return;
        }
        
        // In normal mode, open tiled window for drill-down navigation
        handleOpenInTiledWindow();
    }, [isReplayMode, onReplayNodeClick, id, parentMapNodeId, parentParallelInfo, handleOpenInTiledWindow]);

    const config = resourceConfig[resourceType];
    
    // Sub-workflow key for navigation
    const subWorkflowKey = isSubWorkflow ? state?.Resource?.replace('workflow:', '') : null;

    // Check for resource validation error (resource not found in registry)
    const hasResourceError = Boolean(resourceError);
    // Inferred type (from naming convention) but not confirmed via registry lookup
    const isInferredType = Boolean(explicitResourceType) && !resourceRegistryMatch && !hasResourceError;

    // Determine accent color - use red for errors, dashed for inferred
    const accentColor = hasResourceError ? 'border-l-red-500' : config.accentColor;

    // Determine if node should show replay status styling
    const hasReplayData = isReplayMode && (replayCheckpoint || replayAggregated);
    
    // For map iteration nodes, use aggregated status; otherwise use checkpoint status
    const effectiveReplayStatus = isMapIterationNode && replayAggregated
        ? replayAggregated.aggregatedStatus
        : replayStatus;
    
    const replayBorderColor = hasReplayData
        ? effectiveReplayStatus === 'completed'
            ? 'ring-4 ring-green-500 ring-offset-2'
            : effectiveReplayStatus === 'failed'
            ? 'ring-4 ring-red-500 ring-offset-2'
            : effectiveReplayStatus === 'running'
            ? 'ring-4 ring-blue-500 ring-offset-2 animate-pulse'
            : effectiveReplayStatus === 'partial'
            ? 'ring-4 ring-amber-500 ring-offset-2'
            : ''
        : '';

    return (
        <div className="flex flex-col">
            <WorkflowCard
                selected={selected}
                accentColor={accentColor}
                isStart={isStart}
                isEnd={isEnd}
                handleColor={config.handleColor}
                layoutDirection={layoutDirection}
                isCurrentNode={isCurrentNode}
                isDynamicRoute={isDynamicRoute}
                isDynamicNextConfigured={isDynamicNextConfigured}
                onDynamicHandleClick={onDynamicHandleClick}
                className={cn(
                    isReplayMode && 'cursor-pointer hover:shadow-md transition-shadow',
                    replayBorderColor,
                    // When expanded, remove bottom border radius for seamless connection
                    isExpanded && 'rounded-b-none border-b-0'
                )}
                onClick={handleCardClick}
            >
                {/* Breakpoint Marker (debug mode only) */}
                {isDebugMode && onToggleBreakpoint && (
                    <BreakpointMarker
                        nodeId={id}
                        isEnabled={hasBreakpoint ?? false}
                        isDebugMode={isDebugMode}
                        isCurrent={isCurrentNode ?? false}
                        onClick={onToggleBreakpoint}
                    />
                )}

                {/* Icon - varies by resource type (Agent, Tool, Model, Workflow, Task) */}
                {/* Shows error indicator if resource not found in registry */}
                <WorkflowCard.Icon>
                    {hasResourceError ? (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-red-100 text-red-600">
                                        <AlertTriangle size={20} />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-xs">
                                    <p className="text-sm font-medium text-red-700">Resource Not Found</p>
                                    <p className="text-xs text-slate-600">{resourceError}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : isInferredType ? (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className={cn(
                                            'flex h-10 w-10 items-center justify-center rounded-md opacity-50',
                                            config.bgColor,
                                            config.textColor
                                        )}
                                    >
                                        {config.icon}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-xs">
                                    <p className="text-sm font-medium text-slate-700">Unregistered {config.label}</p>
                                    <p className="text-xs text-slate-500">Type inferred from naming convention. Not found in capability registry.</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        <div
                            className={cn(
                                'flex h-10 w-10 items-center justify-center rounded-md',
                                config.bgColor,
                                config.textColor
                            )}
                        >
                            {config.icon}
                        </div>
                    )}
                </WorkflowCard.Icon>

                {/* Name */}
                <WorkflowCard.Name>
                    <span>{label}</span>
                </WorkflowCard.Name>

                {/* Start badge */}
                {isStart && (
                    <WorkflowCard.Badge className="bg-green-100 text-green-700">Start</WorkflowCard.Badge>
                )}

                {/* Error badge - shown when resource not found in registry */}
                {hasResourceError && (
                    <WorkflowCard.Badge className="bg-red-100 text-red-700">
                        <AlertTriangle size={10} className="mr-0.5" />
                        Error
                    </WorkflowCard.Badge>
                )}

                {/* Capability name displayed in code-like font below title */}
                {/* Show for all known capability types, OR when there's an error (to show invalid resource name in red) */}
                {(resourceType !== 'task' || hasResourceError) && resourceName && (
                    <WorkflowCard.Subtitle className={hasResourceError ? 'text-red-600' : undefined}>
                        <span>{capabilityName}</span>
                        {(resourceType === 'agent' || resourceType === 'tool') && capabilityAppKey && (
                            <span className="block font-mono text-[10px] text-brand">{capabilityAppKey}</span>
                        )}
                    </WorkflowCard.Subtitle>
                )}

                {/* Description / Comment or Resource */}
                <WorkflowCard.Description>
                    {state?.Comment ? (
                        <p className="leading-tight">{state.Comment}</p>
                    ) : resourceType !== 'tool' && state?.Resource ? (
                        <p className="leading-tight text-slate-500">
                            {/* Show clean resource identifier without redundant prefix */}
                            {state.Resource.replace(/^(agent|tool|model|workflow):/, '')}
                        </p>
                    ) : !resourceName ? (
                        <p className="leading-tight text-slate-400 italic">No resource configured</p>
                    ) : null}
                </WorkflowCard.Description>

                {/* Feature Badges (Retry, Catch, Timeout, etc.) */}
                <WorkflowCard.BottomLeft>
                    <StateBadges state={state} />
                </WorkflowCard.BottomLeft>

                {/* Top Right: Expand chevron button + Sub-workflow drill-in button (replay mode only) */}
                <WorkflowCard.TopRight>
                    <div className="flex items-center gap-1">
                        {/* Sub-workflow navigation button (replay mode only) */}
                        {isReplayMode && isSubWorkflow && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 hover:text-purple-700"
                                            title="Open sub-workflow"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenInTiledWindow();
                                            }}
                                        >
                                            <ExternalLink size={12} />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                        <p className="text-sm">Open sub-workflow</p>
                                        <p className="text-xs text-slate-500">
                                            {hasChildRunForReplay 
                                                ? 'View child workflow execution' 
                                                : 'View workflow definition'}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}

                        {/* Expand/Collapse chevron button */}
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={cn(
                                            'h-6 w-6 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600',
                                            isExpanded && 'bg-slate-100 text-slate-600'
                                        )}
                                        title={isExpanded ? 'Collapse pipeline slots' : 'Expand pipeline slots'}
                                        onClick={handleToggleExpand}
                                    >
                                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                    <p className="text-sm">{isExpanded ? 'Collapse' : 'Expand'} pipeline slots</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </WorkflowCard.TopRight>

                {/* Bottom Right: Settings icon (hidden in replay mode - card click opens inspector) */}
                {!isReplayMode && (
                    <WorkflowCard.BottomRight>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                            title="Node Settings"
                            onClick={(e) => {
                                e.stopPropagation();
                                openSettings(data);
                            }}
                        >
                            <Settings size={14} />
                        </Button>
                    </WorkflowCard.BottomRight>
                )}

                {/* Replay status indicator */}
                {isReplayMode && effectiveReplayStatus && (
                    <WorkflowCard.MiddleRight>
                        {/* For map iteration nodes, show aggregated status */}
                        {isMapIterationNode && replayAggregated ? (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className={cn(
                                            "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                                            effectiveReplayStatus === 'completed' && "bg-green-100 text-green-700",
                                            effectiveReplayStatus === 'failed' && "bg-red-100 text-red-700",
                                            effectiveReplayStatus === 'running' && "bg-blue-100 text-blue-700",
                                            effectiveReplayStatus === 'partial' && "bg-amber-100 text-amber-700",
                                            effectiveReplayStatus === 'pending' && "bg-slate-100 text-slate-600",
                                        )}>
                                            <Layers size={12} />
                                            {replayAggregated.completedCount}/{replayAggregated.totalIterations}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs">
                                        <p className="text-sm font-medium">Map Iterations</p>
                                        <div className="text-xs text-slate-600 mt-1">
                                            <p>✓ {replayAggregated.completedCount} completed</p>
                                            {replayAggregated.failedCount > 0 && (
                                                <p className="text-red-600">✗ {replayAggregated.failedCount} failed</p>
                                            )}
                                            {replayAggregated.runningCount > 0 && (
                                                <p>⏳ {replayAggregated.runningCount} running</p>
                                            )}
                                            <p className="mt-1 text-slate-500">Click to inspect iterations</p>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ) : (
                            // Regular checkpoint status
                            <>
                                {effectiveReplayStatus === 'completed' && (
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                                        <CheckCircle2 size={14} className="text-green-600" />
                                    </div>
                                )}
                                {effectiveReplayStatus === 'failed' && (
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
                                        <XCircle size={14} className="text-red-600" />
                                    </div>
                                )}
                                {effectiveReplayStatus === 'running' && (
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
                                        <Clock size={14} className="text-blue-600 animate-pulse" />
                                    </div>
                                )}
                            </>
                        )}
                    </WorkflowCard.MiddleRight>
                )}
            </WorkflowCard>

            {/* Expanded Pipeline Slots Section */}
            {isExpanded && (
                <div className={cn(
                    'w-72 border-2 border-t-0 border-slate-500 rounded-b-xl bg-slate-50 p-3',
                    'border-l-4',
                    hasResourceError ? 'border-l-red-500' : config.accentColor
                )}>
                    {/* Section Header */}
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                            Pipeline Slots
                        </span>
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    {/* Default Capability Slot */}
                    <div className={cn(
                        'border-2 rounded-lg p-3 transition-all duration-200',
                        'border-l-4',
                        hasResourceError 
                            ? 'border-slate-300 bg-red-50 border-l-red-500' 
                            : resourceName 
                                ? cn('border-slate-300 bg-white', config.accentColor)
                                : 'border-slate-300 border-dashed bg-slate-50/50'
                    )}>
                        <div className="flex flex-row items-start gap-3">
                            {/* Capability Icon */}
                            <div className={cn(
                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                hasResourceError 
                                    ? 'bg-red-100 text-red-600'
                                    : resourceName 
                                        ? cn(config.bgColor, config.textColor, isInferredType && 'opacity-50')
                                        : 'bg-slate-100 text-slate-400'
                            )}>
                                {hasResourceError ? (
                                    <AlertTriangle size={16} />
                                ) : resourceType === 'agent' ? (
                                    <Bot size={16} />
                                ) : resourceType === 'tool' ? (
                                    <Wrench size={16} />
                                ) : resourceType === 'model' ? (
                                    <Brain size={16} />
                                ) : resourceType === 'workflow' ? (
                                    <WorkflowIcon size={16} />
                                ) : (
                                    <Play size={16} />
                                )}
                            </div>

                            {/* Content */}
                            <div className="flex flex-1 flex-col min-w-0">
                                <span className={cn(
                                    'text-[10px] font-medium uppercase tracking-wide',
                                    resourceName ? config.textColor : 'text-slate-400'
                                )}>
                                    {config.label}
                                </span>
                                
                                {resourceName ? (
                                    <>
                                        <span className={cn(
                                            'text-sm font-bold truncate',
                                            hasResourceError ? 'text-red-600' : 'text-slate-900'
                                        )}>
                                            {capabilityName}
                                        </span>
                                        {(resourceType === 'agent' || resourceType === 'tool') && capabilityAppKey && (
                                            <span className="font-mono text-[10px] text-brand">{capabilityAppKey}</span>
                                        )}
                                        {hasResourceError && (
                                            <span className="text-[10px] text-red-500">
                                                Not found in registry
                                            </span>
                                        )}
                                        {isInferredType && (
                                            <span className="text-[10px] text-slate-400 italic">
                                                Unregistered
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-xs italic text-slate-400">
                                        No capability configured
                                    </span>
                                )}
                            </div>

                            {/* Navigate button for filled slots */}
                            {resourceName && !hasResourceError && onOpenTiledWindow && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className={cn(
                                                    'h-6 w-6 rounded-full shrink-0',
                                                    config.bgColor,
                                                    config.textColor,
                                                    'hover:opacity-80'
                                                )}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenInTiledWindow();
                                                }}
                                            >
                                                <ExternalLink size={12} />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="left">
                                            <p className="text-sm">Open {config.label.toLowerCase()}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                    </div>

                    {/* Placeholder for future pipeline slots */}
                    <div className="mt-2 text-center">
                        <span className="text-[10px] text-slate-400">
                            Additional middleware slots coming soon
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
});
